import type { SelectOption } from '@ps/web-shared-components';
import { Input, StarsDropDown } from '@ps/web-shared-components';
import {
  getValidatorsForLicense,
  maxLength,
  minLength,
  minNumber,
  numbersOnly,
  required
} from '@ps/web-shared-validators';
import React, { useEffect } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';

import { getCities, getDistricts, getParts, getRegions } from '../utils';

import {
  TXTCLI_City,
  TXTCLI_Country,
  TXTCLI_Optional,
  TXTCLI_Please_enter_ZIP___Postal_Code,
  TXTCLI_Region,
  TXTCLI_Street_Name,
  TXTCLI_Zip_Post_Code,
  TXTWCL_Descriptive_Number,
  TXTWCL_District,
  TXTWCL_Must_be_shorter_than_N0_charac_ELL,
  TXTWCL_Orientation_Number,
  TXTWCL_Part_of_Municipality,
  TXTWCL_Please_enter_District_numberX,
  TXTWCL_Please_enter_your_Descriptive__ELL,
  TXTWCL_Please_enter_your_Street_NameX,
  TXTWCL_Please_enter_your_cityX,
  TXTWCL_Please_select_Part_of_Municipa_ELL,
  TXTWCL_Please_select_RegionX,
  TXTWCL_Street_Name_cannot_be_longer_t_ELL,
  TXTWCL_Street_Name_cannot_be_shorter__ELL,
  TXTWCL_The_value_cannot_contain_only__ELL,
  TXTWCL_Whole_numbers_onlyX_No_decimal_ELL
} from './AddressFieldsCZ.messages.json';

interface Props {
  disabled?: boolean;
  showStates: boolean;
}

const citiesForMunicipalityOptional = ['Opava', 'Liberec'];

const AddressFieldsCZ: React.FC<Props> = ({ disabled, showStates }) => {
  const site = useSelector(select.cms.getSiteId);
  const { formatMessage } = useIntl();
  const { values } = useFormState();
  const { change } = useForm();

  const isDistrictVisible = values.city === 'Praha';
  const cities = getCities(values.state);
  const districts = getDistricts(values.city, values.state);
  const parts = getParts(
    values.city,
    isDistrictVisible ? values.district : districts[0]?.value,
    values.state
  );
  const regions = getRegions();

  useEffect(() => {
    const resetOptionsIfNoValue = (name: string, options?: SelectOption[]) => {
      const noValueInOptions =
        values[name] && !options?.some((part) => part.value === values[name]);
      if (noValueInOptions) change(name, null);
    };
    resetOptionsIfNoValue('partOfMunicipality', parts);
    resetOptionsIfNoValue('district', districts);
    resetOptionsIfNoValue('city', cities);
  });

  return (
    <>
      <Input
        id="streetName"
        label={TXTCLI_Street_Name}
        disabled={disabled}
        validators={[
          required([TXTWCL_Please_enter_your_Street_NameX]),
          minLength(2, { ...TXTWCL_Street_Name_cannot_be_shorter__ELL, values: { 0: 2 } }),
          maxLength(48, { ...TXTWCL_Street_Name_cannot_be_longer_t_ELL, values: { 0: 48 } }),
          ...getValidatorsForLicense(site, 'address')
        ]}
      />
      <Input
        id="buildingNumber"
        label={TXTWCL_Descriptive_Number}
        disabled={disabled}
        validators={[
          required([TXTWCL_Please_enter_your_Descriptive__ELL]),
          maxLength(20),
          numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL, true),
          minNumber(1, TXTWCL_The_value_cannot_contain_only__ELL)
        ]}
      />
      <Input
        id="orientationNumber"
        label={TXTWCL_Orientation_Number}
        disabled={disabled}
        placeholder={formatMessage(TXTCLI_Optional)}
        validators={
          values.orientationNumber
            ? [
                numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL, true),
                maxLength(
                  20,
                  { ...TXTWCL_Must_be_shorter_than_N0_charac_ELL, values: { 0: 21 } },
                  true
                ),
                minNumber(1, TXTWCL_The_value_cannot_contain_only__ELL)
              ]
            : []
        }
      />
      <Input
        id="zip"
        label={TXTCLI_Zip_Post_Code}
        disabled={disabled}
        validators={[required(TXTCLI_Please_enter_ZIP___Postal_Code)]}
      />
      <StarsDropDown
        id="state"
        label={TXTCLI_Region}
        options={regions}
        disabled={disabled}
        validators={[required(TXTWCL_Please_select_RegionX)]}
      />
      <StarsDropDown
        id="city"
        label={TXTCLI_City}
        options={cities}
        disabled={disabled}
        validators={[required(TXTWCL_Please_enter_your_cityX)]}
      />
      {isDistrictVisible && (
        <StarsDropDown
          id="district"
          label={TXTWCL_District}
          options={districts}
          disabled={disabled}
          validators={[required(TXTWCL_Please_enter_District_numberX)]}
        />
      )}
      {!!parts.length && (
        <StarsDropDown
          id="partOfMunicipality"
          label={TXTWCL_Part_of_Municipality}
          options={parts}
          disabled={disabled}
          validators={
            citiesForMunicipalityOptional.includes(values.city)
              ? []
              : [required(TXTWCL_Please_select_Part_of_Municipa_ELL)]
          }
        />
      )}
      {showStates && <Input id="country" label={TXTCLI_Country} disabled />}
    </>
  );
};

export default AddressFieldsCZ;
